import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Brush, Eraser, Undo2, Trash2, Loader2, Plus, Pencil, Layers } from 'lucide-react';
import { COLOR_OJO, fechaCorta } from '../../lib/oftalmo';

/**
 * Dibujo clínico (4/11/2026): «un dibujo de la córnea para dibujar dónde has
 * visto la úlcera… en retina un fondo de ojo, que con el ratón puedas marcar
 * dónde has visto algo… como un pincel que dibuja, la forma que quieras, y
 * luego en el texto al lado “esto es…”».
 *
 * Sobre el esquema del ojo (córnea vista de frente o fondo de ojo), trazos a
 * mano alzada con el ratón, el dedo o el lápiz. Cada trazo lleva su número y
 * su nota. Las coordenadas van de 0 a 100 en el esquema, así el dibujo se ve
 * igual en cualquier pantalla. Derecho e izquierdo, uno al lado del otro.
 */

const COLORES = [
  ['#DC2626', 'Rojo (sangre, hiperemia)'],
  ['#F59E0B', 'Amarillo (exudado, infiltrado)'],
  ['#16A34A', 'Verde (tinción con fluoresceína)'],
  ['#2563EB', 'Azul (edema, líquido)'],
  ['#111827', 'Negro (pigmento, cicatriz)'],
  ['#FFFFFF', 'Blanco (opacidad, leucoma)'],
  ['#9333EA', 'Morado (otros)'],
];
const GROSORES = [[0.7, 'Fino'], [1.6, 'Medio'], [3.5, 'Grueso'], [7, 'Brocha']];
export const PLANTILLAS = { cornea: 'Córnea', fondo: 'Fondo de ojo' };

/** Vasos del fondo de ojo: arcadas temporales que rodean la mácula y ramas
 *  nasales, todas saliendo de la papila. `s` = hacia dónde queda temporal. */
function vasosFondo(papila, macula, s) {
  const v = [];
  for (const g of [-1, 1]) { // superior / inferior
    // Arcada temporal (vena más gruesa y oscura, arteria al lado).
    v.push({ d: `M ${papila} ${50 + g * 1.5} C ${papila + s * 6} ${50 + g * 20}, ${macula - s * 2} ${50 + g * 24}, ${macula + s * 16} ${50 + g * 21} S ${macula + s * 26} ${50 + g * 15}, ${macula + s * 31} ${50 + g * 13}`, ancho: 1.5, color: '#8B1A12' });
    v.push({ d: `M ${papila} ${50 + g * 2.5} C ${papila + s * 8} ${50 + g * 17}, ${macula} ${50 + g * 20}, ${macula + s * 15} ${50 + g * 17} S ${macula + s * 24} ${50 + g * 12}, ${macula + s * 29} ${50 + g * 10}`, ancho: 1.0, color: '#C2412D' });
    // Ramas de la arcada hacia la periferia.
    v.push({ d: `M ${papila + s * 10} ${50 + g * 16} Q ${papila + s * 12} ${50 + g * 28}, ${papila + s * 6} ${50 + g * 40}`, ancho: 0.8, color: '#8B1A12' });
    v.push({ d: `M ${macula} ${50 + g * 22} Q ${macula - s * 2} ${50 + g * 32}, ${macula - s * 8} ${50 + g * 41}`, ancho: 0.7, color: '#C2412D' });
    v.push({ d: `M ${macula + s * 14} ${50 + g * 19} Q ${macula + s * 20} ${50 + g * 28}, ${macula + s * 22} ${50 + g * 36}`, ancho: 0.6, color: '#8B1A12' });
    // Ramitas hacia la mácula (sin llegar a la fóvea).
    v.push({ d: `M ${macula - s * 2} ${50 + g * 21} Q ${macula - s * 1} ${50 + g * 14}, ${macula + s * 1} ${50 + g * 10}`, ancho: 0.35, color: '#C2412D' });
    v.push({ d: `M ${macula + s * 9} ${50 + g * 19} Q ${macula + s * 8} ${50 + g * 13}, ${macula + s * 6} ${50 + g * 9}`, ancho: 0.35, color: '#8B1A12' });
    // Ramas nasales.
    v.push({ d: `M ${papila} ${50 + g * 2} Q ${papila - s * 8} ${50 + g * 10}, ${papila - s * 18} ${50 + g * 22} T ${papila - s * 26} ${50 + g * 36}`, ancho: 1.0, color: '#8B1A12' });
    v.push({ d: `M ${papila} ${50 + g * 1} Q ${papila - s * 10} ${50 + g * 5}, ${papila - s * 24} ${50 + g * 8}`, ancho: 0.7, color: '#C2412D' });
    v.push({ d: `M ${papila - s * 12} ${50 + g * 14} Q ${papila - s * 20} ${50 + g * 14}, ${papila - s * 28} ${50 + g * 18}`, ancho: 0.5, color: '#8B1A12' });
  }
  return v;
}

/** El dibujo de fondo, del ojo que toca (nasal a un lado o al otro):
 *  «cornea», un ojo visto de frente; «fondo», un fondo de ojo típico. */
export function Esquema({ plantilla, ojo }) {
  const nasalDerecha = ojo === 'OD'; // el derecho, visto de frente: la nariz queda a la derecha
  const id = `${plantilla}-${ojo}`;
  if (plantilla === 'fondo') {
    const papila = nasalDerecha ? 67 : 33;
    const macula = nasalDerecha ? 39 : 61;
    const s = nasalDerecha ? -1 : 1;
    return (
      <g>
        <defs>
          <radialGradient id={`fondo-${id}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F2A06B" />
            <stop offset="70%" stopColor="#E07A45" />
            <stop offset="100%" stopColor="#B4532A" />
          </radialGradient>
          <radialGradient id={`macula-${id}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#A9471F" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#A9471F" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`papila-${id}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFF4DC" />
            <stop offset="55%" stopColor="#F9D9A6" />
            <stop offset="100%" stopColor="#F0B97E" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="100" height="100" fill="#111" />
        <circle cx="50" cy="50" r="47" fill={`url(#fondo-${id})`} />
        <circle cx={macula} cy="51" r="9" fill={`url(#macula-${id})`} />
        {vasosFondo(papila, macula, s).map((v, i) => (
          <path key={i} d={v.d} fill="none" stroke={v.color} strokeWidth={v.ancho} strokeLinecap="round" strokeOpacity="0.9" />
        ))}
        <ellipse cx={papila} cy="50" rx="5.2" ry="5.8" fill={`url(#papila-${id})`} stroke="#E8A866" strokeWidth="0.3" />
        <ellipse cx={papila + s * 0.6} cy="50" rx="2.1" ry="2.5" fill="#FFF9EE" />
        <circle cx={macula} cy="51" r="0.7" fill="#FFE2C4" />
        <text x={nasalDerecha ? 90.5 : 5.5} y="52" fontSize="3.4" fill="#FDE7D3" fontWeight="700">N</text>
        <text x={nasalDerecha ? 5.5 : 90.5} y="52" fontSize="3.4" fill="#FDE7D3" fontWeight="700">T</text>
        <text x="48.6" y="8.5" fontSize="3.2" fill="#FDE7D3" fontWeight="700">S</text>
        <text x="49" y="95" fontSize="3.2" fill="#FDE7D3" fontWeight="700">I</text>
      </g>
    );
  }
  // Un ojo visto de frente: párpados con pestañas, carúncula en el lado nasal,
  // esclera, iris con sus fibras, pupila y el reflejo; las horas del reloj
  // alrededor del limbo para situar lo que se dibuja.
  const ancho = 46;
  const nasal = nasalDerecha ? 1 : -1;
  const borde = (y0, curva) => `M ${50 - ancho} 50 Q 50 ${y0 - curva} ${50 + ancho} 50`;
  const fibras = Array.from({ length: 48 }, (_, i) => {
    const a = (i / 48) * 2 * Math.PI;
    return { x1: 50 + 9 * Math.cos(a), y1: 50 + 9 * Math.sin(a), x2: 50 + 20.5 * Math.cos(a + 0.05), y2: 50 + 20.5 * Math.sin(a + 0.05) };
  });
  const pestanas = (arriba) => Array.from({ length: 19 }, (_, i) => {
    const t = (i + 1) / 20;
    const x = 50 - ancho + 2 * ancho * t;
    const y = arriba ? 50 - 2 * 30 * t * (1 - t) * 2 + 0.5 : 50 + 2 * 22 * t * (1 - t) * 2 - 0.5;
    const largo = arriba ? 6 : 3.5;
    return { x, y, x2: x + (t - 0.5) * 4, y2: arriba ? y - largo : y + largo };
  });
  return (
    <g>
      <defs>
        <clipPath id={`hendidura-${id}`}>
          <path d={`${borde(50, 60)} Q 50 ${50 + 44} ${50 - ancho} 50 Z`} />
        </clipPath>
        <radialGradient id={`iris-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="40%" stopColor="#6B8FB5" />
          <stop offset="85%" stopColor="#4A6D94" />
          <stop offset="100%" stopColor="#2F4A6B" />
        </radialGradient>
        <radialGradient id={`esclera-${id}`} cx="50%" cy="50%" r="60%">
          <stop offset="60%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#F1E4E0" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="100" height="100" fill="#F6E3D6" />
      {/* Pliegue del párpado superior y ceja suave */}
      <path d={`M ${50 - ancho - 2} 44 Q 50 ${2} ${50 + ancho + 2} 44`} fill="none" stroke="#D8B4A0" strokeWidth="0.8" />
      <g clipPath={`url(#hendidura-${id})`}>
        <rect x="0" y="0" width="100" height="100" fill={`url(#esclera-${id})`} />
        {/* Vasitos conjuntivales */}
        {[[-1, -6], [-1, 6], [1, -5], [1, 7]].map(([lado, dy], i) => (
          <path key={i} d={`M ${50 + lado * 44} ${50 + dy} q ${-lado * 6} ${dy > 0 ? -2 : 2} ${-lado * 12} ${dy > 0 ? 1 : -1}`} fill="none" stroke="#E7A4A4" strokeWidth="0.35" />
        ))}
        <circle cx="50" cy="50" r="21" fill={`url(#iris-${id})`} />
        {fibras.map((f, i) => <line key={i} {...f} stroke="#9DB7D2" strokeWidth="0.25" strokeOpacity="0.7" />)}
        <circle cx="50" cy="50" r="21" fill="none" stroke="#2B3F57" strokeWidth="0.6" />
        <circle cx="50" cy="50" r="8.5" fill="#0B0F14" />
        <circle cx="45.5" cy="45" r="2.2" fill="#FFFFFF" fillOpacity="0.9" />
      </g>
      {/* Carúncula, en el ángulo nasal */}
      <ellipse cx={50 + nasal * (ancho - 3)} cy="50" rx="2.6" ry="2" fill="#E8A1A1" />
      {/* Bordes de los párpados y pestañas */}
      <path d={borde(50, 60)} fill="none" stroke="#7A4E3A" strokeWidth="1" />
      <path d={`M ${50 - ancho} 50 Q 50 ${50 + 44} ${50 + ancho} 50`} fill="none" stroke="#9A6A55" strokeWidth="0.8" />
      {pestanas(true).map((p, i) => <line key={`a${i}`} x1={p.x} y1={p.y} x2={p.x2} y2={p.y2} stroke="#3B2A22" strokeWidth="0.45" strokeLinecap="round" />)}
      {pestanas(false).map((p, i) => <line key={`b${i}`} x1={p.x} y1={p.y} x2={p.x2} y2={p.y2} stroke="#3B2A22" strokeWidth="0.3" strokeLinecap="round" />)}
      {/* Horas del reloj alrededor del limbo */}
      {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
        const a = (h / 12) * 2 * Math.PI - Math.PI / 2;
        return (
          <text key={h} x={50 + 24.5 * Math.cos(a) - (h >= 10 ? 1.6 : 0.8)} y={50 + 24.5 * Math.sin(a) + 1} fontSize="2.3" fill="#475569" fillOpacity="0.85">{h}</text>
        );
      })}
      <text x={nasalDerecha ? 92 : 4} y="64" fontSize="3.2" fill="#7A4E3A" fontWeight="700">N</text>
      <text x={nasalDerecha ? 4 : 92} y="64" fontSize="3.2" fill="#7A4E3A" fontWeight="700">T</text>
    </g>
  );
}

const ruta = (puntos) => puntos.map(([x, y], i) => `${i ? 'L' : 'M'} ${x} ${y}`).join(' ') + (puntos.length === 1 ? ' l 0.01 0' : '');

/** Los trazos, con su número junto al primer punto. */
export function Trazos({ trazos, numerar = true, opacidad = 1 }) {
  return (
    <g opacity={opacidad}>
      {trazos.map((t, i) => (
        <g key={t.id || i}>
          <path d={ruta(t.puntos)} fill={t.relleno ? t.color : 'none'} fillOpacity={t.relleno ? 0.45 : 0}
            stroke={t.color} strokeWidth={t.grosor} strokeLinecap="round" strokeLinejoin="round"
            strokeOpacity={t.color === '#FFFFFF' ? 0.95 : 0.85}
            style={t.color === '#FFFFFF' ? { filter: 'drop-shadow(0 0 0.4px #475569)' } : undefined} />
          {numerar && (
            <g transform={`translate(${t.puntos[0][0]}, ${t.puntos[0][1]})`}>
              <circle r="2.1" cx="-2.4" cy="-2.4" fill="#111827" opacity="0.85" />
              <text x="-3.3" y="-1.4" fontSize="2.6" fill="#fff" fontWeight="700">{i + 1}</text>
            </g>
          )}
        </g>
      ))}
    </g>
  );
}

function Lienzo({ plantilla, ojo, trazos, setTrazos, anterior, verAnterior, color, grosor, relleno, goma }) {
  const svg = useRef(null);
  const actual = useRef(null);
  const [vivo, setVivo] = useState(null);

  const punto = (e) => {
    const r = svg.current.getBoundingClientRect();
    return [Math.round(((e.clientX - r.left) / r.width) * 10000) / 100, Math.round(((e.clientY - r.top) / r.height) * 10000) / 100];
  };
  const bajar = (e) => {
    if (goma) return;
    e.preventDefault();
    svg.current.setPointerCapture?.(e.pointerId);
    actual.current = { id: `t${Date.now()}`, color, grosor, relleno, puntos: [punto(e)], nota: '' };
    setVivo(actual.current);
  };
  const mover = (e) => {
    if (!actual.current) return;
    const p = punto(e);
    const u = actual.current.puntos.at(-1);
    if (Math.hypot(p[0] - u[0], p[1] - u[1]) < 0.4) return; // sin puntos de más
    actual.current = { ...actual.current, puntos: [...actual.current.puntos, p] };
    setVivo(actual.current);
  };
  const subir = () => {
    if (actual.current) setTrazos((xs) => [...xs, actual.current]);
    actual.current = null;
    setVivo(null);
  };
  return (
    <svg ref={svg} viewBox="0 0 100 100" className={`w-full aspect-square rounded-xl bg-white select-none ${goma ? 'cursor-pointer' : 'cursor-crosshair'}`}
      style={{ touchAction: 'none' }} onPointerDown={bajar} onPointerMove={mover} onPointerUp={subir} onPointerLeave={subir}
      data-testid="oft-lienzo">
      <Esquema plantilla={plantilla} ojo={ojo} />
      {verAnterior && anterior && <Trazos trazos={anterior.trazos || []} numerar={false} opacidad={0.3} />}
      <Trazos trazos={trazos} />
      {goma && trazos.map((t) => (
        <path key={`g${t.id}`} d={ruta(t.puntos)} fill="none" stroke="transparent" strokeWidth={Math.max(4, t.grosor + 3)}
          onClick={() => setTrazos((xs) => xs.filter((x) => x.id !== t.id))} style={{ cursor: 'pointer' }} />
      ))}
      {vivo && <Trazos trazos={[vivo]} numerar={false} />}
    </svg>
  );
}

function DibujoDialog({ open, onOpenChange, pid, plantilla, ojo, registro, anterior, onGuardado }) {
  const [trazos, setTrazos] = useState([]);
  const [titulo, setTitulo] = useState('');
  const [notas, setNotas] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [color, setColor] = useState(COLORES[0][0]);
  const [grosor, setGrosor] = useState(GROSORES[1][0]);
  const [relleno, setRelleno] = useState(false);
  const [goma, setGoma] = useState(false);
  const [verAnterior, setVerAnterior] = useState(true);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTrazos(registro?.trazos ? JSON.parse(JSON.stringify(registro.trazos)) : []);
    setTitulo(registro?.titulo || '');
    setNotas(registro?.notas || '');
    setFecha(registro?.fecha || new Date().toISOString().slice(0, 10));
    setGoma(false);
  }, [open, registro]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const payload = { tipo: 'dibujo', fecha, ojo, plantilla, titulo, notas, trazos };
      const r = registro
        ? await api.put(`/api/patients/${pid}/oftalmologia/registros/${registro.id}`, payload)
        : await api.post(`/api/patients/${pid}/oftalmologia/registros`, payload);
      toast.success('Dibujo guardado en la historia');
      onGuardado(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el dibujo');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="oft-dibujo-dialog">
        <DialogHeader>
          <DialogTitle>
            Dibujo · {PLANTILLAS[plantilla]} <span style={{ color: COLOR_OJO[ojo] }}>{ojo === 'OD' ? 'ojo derecho (OD)' : 'ojo izquierdo (OI)'}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {COLORES.map(([c, t]) => (
                <button key={c} type="button" title={t} onClick={() => { setColor(c); setGoma(false); }}
                  className={`w-7 h-7 rounded-full border-2 ${color === c && !goma ? 'ring-2 ring-offset-1 ring-[#2E6E5E]' : ''}`}
                  style={{ background: c, borderColor: c === '#FFFFFF' ? '#94A3B8' : c }} aria-label={t} />
              ))}
              <span className="mx-1 h-6 w-px bg-border" />
              {GROSORES.map(([g, t]) => (
                <Button key={g} size="sm" variant={grosor === g && !goma ? 'default' : 'outline'} onClick={() => { setGrosor(g); setGoma(false); }}>
                  <Brush className="w-3.5 h-3.5 mr-1" />{t}
                </Button>
              ))}
              <Button size="sm" variant={relleno ? 'default' : 'outline'} onClick={() => setRelleno(!relleno)} title="Las formas cerradas se rellenan">
                Rellenar
              </Button>
              <Button size="sm" variant={goma ? 'default' : 'outline'} onClick={() => setGoma(!goma)} title="Toca un trazo para borrarlo" data-testid="oft-goma">
                <Eraser className="w-3.5 h-3.5 mr-1" />Borrar trazo
              </Button>
              <Button size="sm" variant="outline" disabled={!trazos.length} onClick={() => setTrazos((xs) => xs.slice(0, -1))}><Undo2 className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" disabled={!trazos.length} onClick={() => window.confirm('¿Borrar todo el dibujo?') && setTrazos([])}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
            <Lienzo plantilla={plantilla} ojo={ojo} trazos={trazos} setTrazos={setTrazos} anterior={anterior}
              verAnterior={verAnterior} color={color} grosor={grosor} relleno={relleno} goma={goma} />
            <div className="text-xs text-muted-foreground mt-1">
              {goma ? 'Toca el trazo que quieras borrar.' : 'Dibuja con el ratón, el dedo o el lápiz. Cada trazo lleva un número; escribe al lado qué es.'}
            </div>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Fecha
                <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="h-9" />
              </label>
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Título
                <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Úlcera, hemorragias…" className="h-9" />
              </label>
            </div>
            {anterior && (
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={verAnterior} onChange={(e) => setVerAnterior(e.target.checked)} />
                <Layers className="w-3.5 h-3.5" />Ver debajo el dibujo anterior ({fechaCorta(anterior.fecha)})
              </label>
            )}
            <div className="space-y-2" data-testid="oft-notas-trazos">
              <div className="text-xs font-semibold">Qué es cada trazo</div>
              {!trazos.length && <div className="text-xs text-muted-foreground">Aún no hay trazos.</div>}
              {trazos.map((t, i) => (
                <div key={t.id} className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold text-white shrink-0" style={{ background: '#111827' }}>{i + 1}</span>
                  <span className="w-3 h-3 rounded-sm shrink-0 border" style={{ background: t.color }} />
                  <Input value={t.nota || ''} placeholder="Esto es…" className="h-8 text-xs"
                    onChange={(e) => setTrazos((xs) => xs.map((x) => (x.id === t.id ? { ...x, nota: e.target.value } : x)))} />
                </div>
              ))}
            </div>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Comentario
              <textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={3}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando} data-testid="oft-guardar-dibujo">
            {guardando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Guardar dibujo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Miniatura de un dibujo guardado (con su leyenda). */
function Miniatura({ d, onAbrir }) {
  return (
    <button type="button" onClick={onAbrir} className="text-left rounded-lg border p-1.5 hover:border-[#2E6E5E] w-full" data-testid="oft-dibujo-mini">
      <svg viewBox="0 0 100 100" className="w-full aspect-square rounded-md bg-white">
        <Esquema plantilla={d.plantilla} ojo={d.ojo} />
        <Trazos trazos={d.trazos || []} />
      </svg>
      <div className="text-xs mt-1"><b>{fechaCorta(d.fecha)}</b> {d.titulo && `· ${d.titulo}`}</div>
    </button>
  );
}

/**
 * Los dibujos de una sección (córnea o fondo), ojo derecho e izquierdo uno
 * al lado del otro: el último, grande, con su leyenda; los anteriores, en
 * miniatura debajo.
 */
export default function DibujosOculares({ pid, plantilla, registros, puedeEditar, onGuardado, onBorrar }) {
  const [abierto, setAbierto] = useState(null); // { ojo, registro }
  const dibujos = useMemo(() => (registros || []).filter((r) => r.tipo === 'dibujo' && r.plantilla === plantilla)
    .sort((a, b) => (b.fecha + (b.created_at || '')).localeCompare(a.fecha + (a.created_at || ''))), [registros, plantilla]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`oft-dibujos-${plantilla}`}>
      {['OD', 'OI'].map((ojo) => {
        const delOjo = dibujos.filter((d) => d.ojo === ojo);
        const ultimo = delOjo[0];
        return (
          <div key={ojo} className="rounded-xl border p-3" style={{ borderColor: ojo === 'OD' ? '#FECACA' : '#BFDBFE' }} data-testid={`oft-dibujo-${plantilla}-${ojo}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm" style={{ color: COLOR_OJO[ojo] }}>{ojo === 'OD' ? 'Ojo derecho (OD)' : 'Ojo izquierdo (OI)'}</div>
              {puedeEditar && (
                <Button size="sm" variant="outline" onClick={() => setAbierto({ ojo, registro: null })} data-testid={`oft-nuevo-dibujo-${plantilla}-${ojo}`}>
                  <Plus className="w-4 h-4 mr-1" />Nuevo dibujo
                </Button>
              )}
            </div>
            {ultimo ? (
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
                <div>
                  <svg viewBox="0 0 100 100" className="w-full aspect-square rounded-lg bg-white border">
                    <Esquema plantilla={plantilla} ojo={ojo} />
                    <Trazos trazos={ultimo.trazos || []} />
                  </svg>
                </div>
                <div className="text-xs space-y-1">
                  <div className="font-semibold">{fechaCorta(ultimo.fecha)} {ultimo.titulo && `· ${ultimo.titulo}`}</div>
                  {(ultimo.trazos || []).map((t, i) => (
                    <div key={t.id || i} className="flex items-start gap-1.5">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold text-white shrink-0 bg-gray-900">{i + 1}</span>
                      <span className="w-2.5 h-2.5 mt-0.5 rounded-sm border shrink-0" style={{ background: t.color }} />
                      <span>{t.nota || <i className="text-muted-foreground">sin nota</i>}</span>
                    </div>
                  ))}
                  {ultimo.notas && <div className="text-muted-foreground pt-1">{ultimo.notas}</div>}
                  <div className="text-muted-foreground">{ultimo.created_by_name}</div>
                  {puedeEditar && (
                    <div className="flex gap-1 pt-1">
                      <Button size="sm" variant="ghost" onClick={() => setAbierto({ ojo, registro: ultimo })}><Pencil className="w-3.5 h-3.5 mr-1" />Editar</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onBorrar(ultimo)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button type="button" disabled={!puedeEditar} onClick={() => setAbierto({ ojo, registro: null })}
                className="w-full rounded-lg border border-dashed p-2 text-xs text-muted-foreground hover:border-[#2E6E5E]">
                <svg viewBox="0 0 100 100" className="w-40 mx-auto aspect-square opacity-60"><Esquema plantilla={plantilla} ojo={ojo} /></svg>
                Sin dibujos. {puedeEditar ? 'Toca para dibujar.' : ''}
              </button>
            )}
            {delOjo.length > 1 && (
              <div className="mt-3">
                <div className="text-xs text-muted-foreground mb-1">Anteriores</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {delOjo.slice(1).map((d) => <Miniatura key={d.id} d={d} onAbrir={() => puedeEditar && setAbierto({ ojo, registro: d })} />)}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {abierto && (
        <DibujoDialog open={!!abierto} onOpenChange={(o) => !o && setAbierto(null)} pid={pid} plantilla={plantilla} ojo={abierto.ojo}
          registro={abierto.registro}
          anterior={dibujos.find((d) => d.ojo === abierto.ojo && d.id !== abierto.registro?.id && (!abierto.registro || d.fecha <= abierto.registro.fecha))}
          onGuardado={onGuardado} />
      )}
    </div>
  );
}
